import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalAddProspectCompany.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import TextInput from "../TextInput";
import { showSuccess, showError } from "../../services/toast.service";
import { createCompany } from "../../services/company.service";
import CompanySearchBar from "../CompanySearchBar";

const ModalAssignCompany = ({ visible, onClose, onSave, folderId }) => {
  const modalRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    representative_lastname: '',
    representative_firstname: '',
    representative_email: '',
  });

  // Gère le défilement du corps et réinitialise le formulaire
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-add-company");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
      setIsSubmitting(false);
    } else {
      clearAllBodyScrollLocks();
      // Réinitialise le formulaire à la fermeture
      setFormData({
        company_name: '',
        representative_lastname: '',
        representative_firstname: '',
        representative_email: '',
      });
      setSelectedCompany(null);
      setShowCreateForm(false);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  // Gère la sélection d'une entreprise existante
  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setShowCreateForm(false);
  };

  // Gère l'association d'une entreprise existante au dossier
  const handleAssignExistingCompany = async () => {
    if (!selectedCompany || !folderId) return;

    setIsSubmitting(true);
    try {
      // Importer le service updateDraftFolder
      const { updateDraftFolder } = await import('../../services/folder.service');
      
      await updateDraftFolder(folderId, {
        company_id: selectedCompany.id
      });

      showSuccess("Entreprise associée avec succès");
      
      if (onSave) {
        onSave(selectedCompany);
      }
      
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'association de l'entreprise:", error);
      showError("Erreur lors de l'association de l'entreprise");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gère les changements dans les champs du formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validation des champs requis
  const validateForm = () => {
    const requiredFields = ['company_name', 'representative_lastname', 'representative_firstname', 'representative_email'];
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());

    if (missingFields.length > 0) {
      showError(`Champs manquants: ${missingFields.join(', ')}`);
      return false;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.representative_email)) {
      showError("Veuillez saisir un email valide");
      return false;
    }

    return true;
  };

  // Gère la soumission du formulaire pour créer une nouvelle entreprise
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Préparer les données pour l'API
      const companyData = {
        name: formData.company_name,
        email: formData.representative_email,
        director_email: formData.representative_email,
        director_first_name: formData.representative_firstname,
        director_last_name: formData.representative_lastname,
      };

      const result = await createCompany(companyData);

      if (result?.success && result?.company?.id) {
        // Associer la nouvelle entreprise au dossier
        if (folderId) {
          const { updateDraftFolder } = await import('../../services/folder.service');
          await updateDraftFolder(folderId, {
            company_id: result.company.id
          });
        }

        showSuccess("Entreprise créée et associée avec succès");

        if (onSave) {
          onSave(result.company);
        }

        onClose();
      } else {
        throw new Error('Échec de la création de l\'entreprise');
      }
    } catch (error) {
      console.error("Erreur lors de la création de l'entreprise:", error);
      showError(error.message || "Une erreur est survenue lors de la création de l'entreprise");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ne rend le portail que si la modale est visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <>
      <div id="modal-add-company" className={styles.modal} ref={modalRef}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <Card
            className={styles.card}
            title="Ajouter une entreprise"
            classTitle="title-blue"
          >
            <div className={styles.container}>
              <p className={styles.sectionDescription}>Rechercher une société existante ou créer une nouvelle société</p>

              <CompanySearchBar
                label="Rechercher une entreprise"
                onSelectCompany={handleSelectCompany}
                showDetails={true}
              />

              {selectedCompany && (
                <div className={styles.selectedCompanySection}>
                  <div className={styles.btnWrap}>
                    <button 
                      type="button" 
                      className="button"
                      onClick={handleAssignExistingCompany}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Association en cours..." : "Associer cette entreprise"}
                    </button>
                    <button 
                      type="button" 
                      className="button-stroke"
                      onClick={() => setShowCreateForm(true)}
                    >
                      Créer une nouvelle entreprise
                    </button>
                  </div>
                </div>
              )}

              {(!selectedCompany || showCreateForm) && (
                <>
                  <div className={styles.divider} />
                  <form className={styles.form} onSubmit={handleSubmit}>


                {/* Informations sur l'entreprise */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Informations sur l'entreprise</h3>

                  <TextInput
                    className={styles.field}
                    label="Nom de l'entreprise"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.divider} />

                {/* Informations sur le représentant */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Représentant de l'entreprise</h3>
                  <p className={styles.sectionDescription}>Cette personne aura accès au compte entreprise</p>

                  <div className={styles.group}>
                    <TextInput
                      className={styles.field}
                      label="Nom"
                      name="representative_lastname"
                      value={formData.representative_lastname}
                      onChange={handleChange}
                      required
                    />
                    <TextInput
                      className={styles.field}
                      label="Prénom"
                      name="representative_firstname"
                      value={formData.representative_firstname}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <TextInput
                    className={styles.field}
                    label="Email professionnel"
                    name="representative_email"
                    type="email"
                    value={formData.representative_email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.divider} />

                {/* Boutons d'action */}
                <div className={styles.btnWrap}>
                  <button type="button" className="button-stroke" onClick={onClose} disabled={isSubmitting}>
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Création en cours..." : "Créer l'entreprise"}
                  </button>
                  </div>
                </form>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ModalAssignCompany;